import {Alert, Button, Card, type ColorSchemeType, Tag} from 'platform/components';
import {
  Box,
  Center,
  HStack,
  Icon,
  Show,
  Text,
  type ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {type MyDayData} from '../../../types/MyDayData';

interface MyDayHistoryProps {
  history: NonNullable<MyDayData['history']>;
  isDisabled: boolean;
  onLoadMore?: () => void;
  onUndo?: (decisionId: string) => void;
}

export function MyDayHistory(props: MyDayHistoryProps) {
  return (
    <Card
      title={i18n.t('page.interestMyDay.history.title')}
      titleIcon="action/check_circle_outline"
      isExpandable
    >
      <VStack spacing={4}>
        <Text size="xSmall" color="tertiary">
          {props.history.day} · {props.history.timeZone}
        </Text>
        <Show when={!props.history.isComplete}>
          <Alert variant="warning" message={i18n.t('page.interestMyDay.coverage.incomplete')} />
        </Show>
        <Show when={props.history.entries.length === 0}>
          <Text>{i18n.t('general.labels.noData')}</Text>
        </Show>
        {props.history.entries.map((entry) => {
          const occurredAt = parseDate(entry.occurredAt);
          const outcome = match(entry.outcome)
            .returnType<{icon: ThemeIconKey; colorScheme: ColorSchemeType; label: string}>()
            .with('completed', () => ({
              icon: 'navigation/check',
              colorScheme: 'green',
              label: i18n.t('page.datagrid.labels.done'),
            }))
            .with('attempted', () => ({
              icon: 'communication/call',
              colorScheme: 'orange',
              label: i18n.t('page.interestMyDay.history.attempted'),
            }))
            .with('rejected', () => ({
              icon: 'navigation/close',
              colorScheme: 'neutral',
              label: i18n.t('entity.automationRule.approval.statusRejected'),
            }))
            .exhaustive();

          return (
            <HStack key={entry.id} spacing={3} align="center" justify="space-between" wrap>
              <HStack spacing={3} align="flex-start" grow={1}>
                <Box
                  width={8}
                  height={8}
                  flexShrink={0}
                  borderRadius="circular"
                  backgroundColor={
                    entry.outcome === 'completed'
                      ? 'palettes.green.10.100'
                      : 'palettes.neutral.20.100'
                  }
                >
                  <Center height="100%">
                    <Icon
                      value={outcome.icon}
                      size={4}
                      color={entry.outcome === 'completed' ? 'text.success' : 'text.secondary'}
                    />
                  </Center>
                </Box>
                <VStack spacing={1}>
                  <Text alternative>{entry.customerLabel}</Text>
                  <HStack spacing={2} wrap>
                    <Text size="small" color="secondary">
                      {entry.actorLabel}
                    </Text>
                    <Text size="small" color="secondary">
                      <DateTimeFormat value={occurredAt} format="dateMedium" />
                      {' · '}
                      <DateTimeFormat value={occurredAt} format="timeLong" />
                    </Text>
                  </HStack>
                  <Show when={entry.rejectionReason}>
                    {(reason) => (
                      <Text size="small">
                        {match(reason)
                          .with('ALREADY_HANDLED', () =>
                            i18n.t('entity.interestRecommendations.rejectReasons.alreadyHandled')
                          )
                          .with('NOT_INTERESTED', () =>
                            i18n.t('entity.interestRecommendations.rejectReasons.notInterested')
                          )
                          .with('BAD_TIMING', () =>
                            i18n.t('entity.interestRecommendations.rejectReasons.badTiming')
                          )
                          .with('DISAGREE_PRIORITY', () =>
                            i18n.t('entity.interestRecommendations.rejectReasons.disagreePriority')
                          )
                          .with('WRONG_RECOMMENDATION', () =>
                            i18n.t(
                              'entity.interestRecommendations.rejectReasons.wrongRecommendation'
                            )
                          )
                          .exhaustive()}
                      </Text>
                    )}
                  </Show>
                </VStack>
              </HStack>
              <HStack spacing={2} align="center">
                <Tag
                  isSubtle
                  size="small"
                  colorScheme={outcome.colorScheme}
                  label={outcome.label}
                />
                <Show when={entry.outcome === 'rejected' && entry.canUndo && props.onUndo}>
                  <Button
                    variant="link"
                    size="small"
                    title={i18n.t('entity.automationRule.toast.undo')}
                    isDisabled={props.isDisabled}
                    onClick={() => props.onUndo?.(entry.id)}
                  />
                </Show>
              </HStack>
            </HStack>
          );
        })}
        <Show when={props.history.hasMore}>
          <Button
            variant="secondary"
            title={i18n.t('entity.automationRule.list.loadMore')}
            isDisabled={props.isDisabled || !props.onLoadMore}
            onClick={props.onLoadMore}
          />
        </Show>
      </VStack>
    </Card>
  );
}
